import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Settings, Save, Loader2, Facebook, RefreshCw, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useSocial } from '../contexts/SocialContext';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const KEYS = [
  { id: 'yearsExperience',    title: 'Jahre Erfahrung',       hint: 'z. B. "15+"' },
  { id: 'successfulProjects', title: 'Erfolgreiche Projekte', hint: 'z. B. "86+"' },
  { id: 'investmentVolume',   title: 'Investitionsvolumen',   hint: 'z. B. "CHF 97M+"' },
];

const DEFAULT_STATS = {
  yearsExperience:    { value: '15+',      label: 'Jahre Erfahrung' },
  successfulProjects: { value: '86+',      label: 'Erfolgreiche Projekte' },
  investmentVolume:   { value: 'CHF 97M+', label: 'Investitionsvolumen' },
};

const SOCIAL_FIELDS = [
  { id: 'facebookUrl',     title: 'Facebook URL',          hint: 'https://www.facebook.com/...' },
  { id: 'facebookPageId',  title: 'Facebook Page ID',      hint: 'für eingebettetes Feed-Plugin (z. B. 61590111810532)' },
  { id: 'instagramUrl',    title: 'Instagram URL',         hint: 'https://www.instagram.com/...' },
  { id: 'linkedinUrl',     title: 'LinkedIn URL',          hint: 'https://www.linkedin.com/company/...' },
  { id: 'twitterUrl',      title: 'X / Twitter URL',       hint: 'https://twitter.com/...' },
  { id: 'youtubeUrl',      title: 'YouTube URL',           hint: 'https://www.youtube.com/@...' },
  { id: 'fbPixelId',       title: 'Facebook Pixel ID',     hint: 'aus Meta Events Manager (z. B. 1234567890)' },
  { id: 'ogImageUrl',      title: 'Vorschau-Bild URL (OG)', hint: 'erscheint beim Teilen auf FB/WhatsApp – 1200×630px' },
  { id: 'ogTitle',         title: 'Vorschau-Titel (OG)',   hint: 'max. ~70 Zeichen' },
  { id: 'ogDescription',   title: 'Vorschau-Beschreibung', hint: 'max. ~160 Zeichen' },
];

export const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { social: socialFromCtx, reload: reloadSocial } = useSocial();
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [social, setSocial] = useState(socialFromCtx);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSocial, setSavingSocial] = useState(false);
  const [aregStatus, setAregStatus] = useState(null);
  const [refreshingAreg, setRefreshingAreg] = useState(false);
  const [finance, setFinance] = useState({
    nominalValue: 100, issuePrice: 110, currency: 'CHF',
    minSubscriptionShares: 27,
    bankBeneficiary: 'Löwenherz Invest AG', bankName: 'UBS AG',
    bankIBAN: '', bankBIC: '',
  });
  const [savingFinance, setSavingFinance] = useState(false);
  const [welcomeMail, setWelcomeMail] = useState({ enabled: true, introText: '' });
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [testingWelcome, setTestingWelcome] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    (async () => {
      try {
        const r = await fetch(apiBase() + '/api/site/stats');
        const data = await r.json();
        if (data?.success && data.stats) setStats({ ...DEFAULT_STATS, ...data.stats });
      } catch (e) {
        toast.error('Fehler beim Laden der Statistiken');
      }
      try {
        const r = await fetch(apiBase() + '/api/site/social');
        const data = await r.json();
        if (data?.success && data.social) setSocial(data.social);
      } catch (_e) { /* keep defaults */ }
      // Areg cache & Finance: admin-only
      if (user.role === 'admin') {
        try {
          const r = await fetch(apiBase() + '/api/admin/areg/cache/status', { credentials: 'include' });
          const data = await r.json();
          if (data?.success) setAregStatus(data);
        } catch (_e) { /* ok */ }
        try {
          const r = await fetch(apiBase() + '/api/admin/site/finance', { credentials: 'include' });
          const data = await r.json();
          if (data?.success && data.finance) setFinance({ ...finance, ...data.finance });
        } catch (_e) { /* keep defaults */ }
      }
      try {
        const r = await fetch(apiBase() + '/api/admin/site/welcome-mail', { credentials: 'include' });
        const data = await r.json();
        if (data?.success && data.settings) setWelcomeMail(data.settings);
      } catch (_e) { /* keep defaults */ }
      setLoading(false);
    })();
  }, [user, navigate]);

  const update = (key, field, val) => {
    setStats((s) => ({ ...s, [key]: { ...s[key], [field]: val } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/site/stats', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stats }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Speichern fehlgeschlagen');
      toast.success('Statistiken aktualisiert – auf der Startseite jetzt sichtbar');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (window.confirm('Auf Standardwerte zurücksetzen?')) {
      setStats(DEFAULT_STATS);
    }
  };

  const saveSocial = async () => {
    setSavingSocial(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/site/social', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ social }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Speichern fehlgeschlagen');
      if (data?.social) setSocial(data.social);
      await reloadSocial();
      toast.success('Social-Media-Einstellungen aktualisiert');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingSocial(false);
    }
  };

  const refreshAreg = async () => {
    setRefreshingAreg(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/areg/cache/refresh', {
        method: 'POST',
        credentials: 'include',
      });      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Refresh fehlgeschlagen');
      toast.success(`Areg-Cache aktualisiert (${data.summary?.tranchesCount ?? 0} Tranchen)`);
      // Status neu laden
      const s = await fetch(apiBase() + '/api/admin/areg/cache/status', { credentials: 'include' }).then(r => r.json());
      if (s?.success) setAregStatus(s);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRefreshingAreg(false);
    }
  };

  const saveFinance = async () => {
    setSavingFinance(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/site/finance', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ finance }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Speichern fehlgeschlagen');
      if (data?.finance) setFinance({ ...finance, ...data.finance });
      toast.success('Finanz-Einstellungen aktualisiert');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingFinance(false);
    }
  };

  const saveWelcome = async () => {
    setSavingWelcome(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/site/welcome-mail', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: welcomeMail }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Speichern fehlgeschlagen');
      if (data?.settings) setWelcomeMail(data.settings);
      toast.success('Willkommens-Mail Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingWelcome(false);
    }
  };

  const testWelcome = async () => {
    setTestingWelcome(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/site/welcome-mail/test', {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Test-Mail fehlgeschlagen');
      toast.success(`Test-Mail an ${data.to} gesendet`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTestingWelcome(false);
    }
  };

  return (
    <div className="bg-black min-h-screen text-white">
      <header className="bg-[#1a1a1a] border-b border-[#C4A55A]/30 p-4">
        <div className="container mx-auto flex justify-between items-center">
          <Link to="/admin/dashboard" className="flex items-center text-[#C4A55A] hover:text-[#D4B56A]">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Admin-Dashboard
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl" data-testid="page-admin-settings">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="h-9 w-9 text-[#C4A55A]" />
          <h1 className="text-3xl md:text-4xl font-bold">Webseiten-Einstellungen</h1>
        </div>
        <p className="text-gray-400 mb-8 leading-relaxed">
          Anpassen der drei Statistik-Kacheln im oberen Bereich der Startseite. Änderungen werden
          sofort öffentlich sichtbar.
        </p>

        {loading ? (
          <div className="text-gray-400 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Lade…
          </div>
        ) : (
          <>
            <section className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8 mb-6">
              <h2 className="text-xl font-semibold mb-5">Hero-Statistiken</h2>

              <div className="space-y-5">
                {KEYS.map((k) => (
                  <div key={k.id} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-5 border-b border-white/10 last:border-0 last:pb-0">
                    <div className="sm:col-span-2 flex items-center justify-between">
                      <h3 className="text-sm uppercase tracking-wider text-[#C4A55A] font-medium">{k.title}</h3>
                      <span className="text-xs text-gray-500">{k.hint}</span>
                    </div>
                    <label className="block">
                      <span className="text-sm text-gray-300">Wert (gross angezeigt)</span>
                      <input
                        type="text"
                        value={stats[k.id]?.value || ''}
                        onChange={(e) => update(k.id, 'value', e.target.value)}
                        className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white text-lg font-semibold focus:border-[#C4A55A] focus:outline-none"
                        data-testid={`settings-${k.id}-value`}
                        maxLength={32}
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-300">Beschriftung (darunter)</span>
                      <input
                        type="text"
                        value={stats[k.id]?.label || ''}
                        onChange={(e) => update(k.id, 'label', e.target.value)}
                        className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                        data-testid={`settings-${k.id}-label`}
                        maxLength={64}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </section>

            {/* Live preview */}
            <section className="bg-gradient-to-br from-[#1a1a1a] via-black to-[#1a1a1a] border border-[#C4A55A]/30 rounded-2xl p-6 md:p-8 mb-6">
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-4">Live-Vorschau</div>
              <div className="grid grid-cols-3 gap-4">
                {KEYS.map((k) => (
                  <div key={k.id} className="text-center">
                    <div className="text-3xl font-bold text-[#C4A55A] mb-1">{stats[k.id]?.value}</div>
                    <div className="text-gray-400 text-xs">{stats[k.id]?.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <div className="flex flex-wrap justify-end gap-3">
              <button
                onClick={reset}
                className="px-4 py-2 rounded-lg bg-[#1a1a1a] border border-white/10 hover:bg-white/5 text-gray-200 text-sm"
                data-testid="settings-reset"
              >
                Auf Standard zurücksetzen
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="settings-save"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Wird gespeichert…' : 'Speichern'}
              </button>
            </div>

            {/* === Social Media & Marketing === */}
            <section className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8 mt-10 mb-6" data-testid="settings-social-section">
              <div className="flex items-center gap-3 mb-5">
                <Facebook className="h-6 w-6 text-[#1877F2]" />
                <h2 className="text-xl font-semibold">Social Media & Marketing</h2>
              </div>
              <p className="text-sm text-gray-400 mb-6">
                Diese Werte erscheinen im Footer, Header, im Facebook-Feed-Embed und in den
                Vorschau-Karten beim Teilen auf WhatsApp / Facebook / LinkedIn. Felder leer lassen,
                um sie auszublenden.
              </p>

              <div className="space-y-4">
                {SOCIAL_FIELDS.map((f) => (
                  <label key={f.id} className="block">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-200">{f.title}</span>
                      <span className="text-xs text-gray-500">{f.hint}</span>
                    </div>
                    {f.id === 'ogDescription' ? (
                      <textarea
                        value={social[f.id] || ''}
                        onChange={(e) => setSocial({ ...social, [f.id]: e.target.value })}
                        rows={2}
                        className="w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none resize-y"
                        data-testid={`settings-social-${f.id}`}
                        maxLength={512}
                      />
                    ) : (
                      <input
                        type="text"
                        value={social[f.id] || ''}
                        onChange={(e) => setSocial({ ...social, [f.id]: e.target.value })}
                        className="w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                        data-testid={`settings-social-${f.id}`}
                        maxLength={512}
                      />
                    )}
                  </label>
                ))}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={saveSocial}
                  disabled={savingSocial}
                  data-testid="settings-save-social"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#1877F2] to-[#0c5fc4] hover:from-[#1e83ff] hover:to-[#1372e0] text-white font-semibold disabled:opacity-50"
                >
                  {savingSocial ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {savingSocial ? 'Wird gespeichert…' : 'Social-Einstellungen speichern'}
                </button>
              </div>
            </section>

            {/* === Finanz-Einstellungen (Ausgabepreis, Nennwert, Bankverbindung) === */}
            {isAdmin && (
            <section className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8 mt-10 mb-6" data-testid="settings-finance-section">
              <div className="flex items-center gap-3 mb-3">
                <Settings className="h-6 w-6 text-[#C4A55A]" />
                <h2 className="text-xl font-semibold">Finanz-Einstellungen</h2>
              </div>
              <p className="text-sm text-gray-400 mb-6">
                Ausgabepreis, Nennwert und Bankverbindung. Diese Werte werden in Bezugsrechts-Mails,
                Zeichnungsscheinen und im Investor-Dashboard verwendet.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <label className="block">
                  <span className="text-sm text-gray-300">Nennwert pro Aktie (CHF)</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={finance.nominalValue}
                    onChange={(e) => setFinance({ ...finance, nominalValue: parseFloat(e.target.value) || 0 })}
                    className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white text-lg font-semibold focus:border-[#C4A55A] focus:outline-none"
                    data-testid="settings-finance-nominal"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-gray-300">Ausgabepreis pro Aktie (CHF)</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={finance.issuePrice}
                    onChange={(e) => setFinance({ ...finance, issuePrice: parseFloat(e.target.value) || 0 })}
                    className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white text-lg font-semibold focus:border-[#C4A55A] focus:outline-none"
                    data-testid="settings-finance-issueprice"
                  />
                  {finance.issuePrice > finance.nominalValue && (
                    <span className="text-xs text-emerald-400 mt-1 inline-block">
                      Agio: CHF {(finance.issuePrice - finance.nominalValue).toFixed(2)} pro Aktie (in gesetzliche Reserven)
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="text-sm text-gray-300">Mindest-Zeichnung (Aktien)</span>
                  <input
                    type="number" min="1"
                    value={finance.minSubscriptionShares}
                    onChange={(e) => setFinance({ ...finance, minSubscriptionShares: parseInt(e.target.value, 10) || 1 })}
                    className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                    data-testid="settings-finance-min-shares"
                  />
                  <span className="text-xs text-gray-500 mt-1 inline-block">
                    Untergrenze bei Teilzeichnung (≈ CHF {((finance.minSubscriptionShares || 0) * (finance.issuePrice || 0)).toFixed(0)})
                  </span>
                </label>
              </div>

              <h3 className="text-sm uppercase tracking-wider text-[#C4A55A] font-medium mb-3 mt-6">Bankverbindung (Zeichnungsschein)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block sm:col-span-2">
                  <span className="text-sm text-gray-300">Zahlungsempfänger</span>
                  <input
                    type="text"
                    value={finance.bankBeneficiary}
                    onChange={(e) => setFinance({ ...finance, bankBeneficiary: e.target.value })}
                    className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                    data-testid="settings-finance-beneficiary"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-gray-300">Bank</span>
                  <input
                    type="text"
                    value={finance.bankName}
                    onChange={(e) => setFinance({ ...finance, bankName: e.target.value })}
                    className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                    data-testid="settings-finance-bankname"
                    placeholder="UBS AG"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-gray-300">BIC / SWIFT (optional)</span>
                  <input
                    type="text"
                    value={finance.bankBIC}
                    onChange={(e) => setFinance({ ...finance, bankBIC: e.target.value.toUpperCase() })}
                    className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none font-mono"
                    data-testid="settings-finance-bic"
                    placeholder="UBSWCHZH80A"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-sm text-gray-300">IBAN</span>
                  <input
                    type="text"
                    value={finance.bankIBAN}
                    onChange={(e) => setFinance({ ...finance, bankIBAN: e.target.value.toUpperCase().replace(/\s+/g, '') })}
                    className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none font-mono"
                    data-testid="settings-finance-iban"
                    placeholder="CH00 0000 0000 0000 0000 0"
                  />
                  {finance.bankIBAN && (
                    <span className="text-xs text-gray-500 mt-1 inline-block">
                      Formatiert: {finance.bankIBAN.replace(/(.{4})/g, '$1 ').trim()}
                    </span>
                  )}
                </label>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={saveFinance}
                  disabled={savingFinance}
                  data-testid="settings-save-finance"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold disabled:opacity-50"
                >
                  {savingFinance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {savingFinance ? 'Wird gespeichert…' : 'Finanz-Einstellungen speichern'}
                </button>
              </div>
            </section>
            )}

            {/* === Willkommens-Mail Einstellungen === */}
            <section className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8 mt-6 mb-6" data-testid="settings-welcome-section">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="h-6 w-6 text-[#C4A55A]" />
                <h2 className="text-xl font-semibold">Willkommens-E-Mail</h2>
                <label className="ml-auto flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!welcomeMail.enabled}
                    onChange={(e) => setWelcomeMail({ ...welcomeMail, enabled: e.target.checked })}
                    className="accent-[#C4A55A] h-4 w-4"
                    data-testid="welcome-enabled"
                  />
                  <span className={welcomeMail.enabled ? 'text-emerald-300' : 'text-gray-500'}>
                    {welcomeMail.enabled ? 'Aktiv' : 'Deaktiviert'}
                  </span>
                </label>
              </div>
              <p className="text-sm text-gray-400 mb-5">
                Wird automatisch versendet, sobald sich ein neuer Investor registriert. Inhalt ist eine professionelle
                Begrüssung mit nächsten Schritten, aktueller Tranche und Link ins Investor-Dashboard.
              </p>
              <label className="block mb-4">
                <span className="text-sm text-gray-300">Persönliche Begrüssung (wird in der Mail prominent angezeigt)</span>
                <textarea
                  rows={4}
                  value={welcomeMail.introText}
                  onChange={(e) => setWelcomeMail({ ...welcomeMail, introText: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C4A55A] focus:outline-none"
                  placeholder="Wir entwickeln seit Jahren ein hochwertiges Schweizer Immobilien-Portfolio..."
                  data-testid="welcome-intro"
                />
                <span className="text-xs text-gray-500 mt-1 inline-block">
                  Erscheint als hervorgehobene Box. Lassen Sie das Feld leer, um keine zusätzliche Box anzuzeigen.
                </span>
              </label>
              <div className="flex justify-end gap-3">
                <button
                  onClick={testWelcome}
                  disabled={testingWelcome}
                  data-testid="welcome-test"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a1a1a] border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/10 text-sm font-medium disabled:opacity-50"
                >
                  {testingWelcome ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Test-Mail an mich senden
                </button>
                <button
                  onClick={saveWelcome}
                  disabled={savingWelcome}
                  data-testid="welcome-save"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold disabled:opacity-50"
                >
                  {savingWelcome ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Speichern
                </button>
              </div>
            </section>

            {/* === Areg-Cache === */}
            {aregStatus?.configured && (
              <section className="bg-[#1a1a1a] border border-emerald-500/25 rounded-2xl p-6 md:p-8 mb-6" data-testid="settings-areg-section">
                <div className="flex items-center gap-3 mb-3">
                  <RefreshCw className="h-6 w-6 text-emerald-400" />
                  <h2 className="text-xl font-semibold">Aktienregister-Cache</h2>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Die Daten aus das-aktienregister.ch werden alle{' '}
                  <strong className="text-white">
                    {Math.round((aregStatus.refreshIntervalSeconds || 1800) / 60)} Min
                  </strong>{' '}
                  automatisch im Hintergrund aktualisiert. Bei Bedarf kannst du sofort
                  einen Sync auslösen (z. B. nach einer Eintragung im Aktienregister).
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-300 mb-4">
                  <span>Stand:</span>
                  <span className="text-white tabular-nums">
                    {aregStatus.ageSeconds !== null && aregStatus.ageSeconds !== undefined
                      ? `vor ${Math.round(aregStatus.ageSeconds / 60)} Min`
                      : 'noch nicht geladen'}
                  </span>
                  {aregStatus.lastUpdate && (
                    <span className="text-gray-500 text-xs">
                      ({new Date(aregStatus.lastUpdate).toLocaleString('de-CH')})
                    </span>
                  )}
                </div>
                <button
                  onClick={refreshAreg}
                  disabled={refreshingAreg}
                  data-testid="settings-refresh-areg"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-semibold disabled:opacity-50"
                >
                  {refreshingAreg ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {refreshingAreg ? 'Wird aktualisiert…' : 'Jetzt aus Aktienregister aktualisieren'}
                </button>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};
